import React, { ChangeEvent, useContext, useMemo, useState } from 'react';
import SettingsPopup from './SettingsPopup';
import ModalDialog, { ModalTransition } from '@atlaskit/modal-dialog';
import BoardContext from '../../../contexts/BoardContext';
import BaseViewsContext from '../../../contexts/BaseViewsContext';
import updateBoard from '../../../api/boards/updateBoard';
import AccountPickerContext from '../../../contexts/AccountPickerContext';
import isCustomerLaneBoard from '../../../isCustomerLaneBoard';
import { CUSTOMER_LANE_COMMITMENTS } from '../../../constants';
import { DEFAULT_CUSTOMER_LANE_PERIODS_DATE_RANGE } from '../../../contextProviders/CustomerLaneBoardPeriodsProvider';

const SettingsPopupContainer = ({
  close,
  board,
}: {
  close: () => void;
  board: CustomerLaneBoard;
}) => {
  const { accountRef } = useContext(AccountPickerContext);
  const { baseViews } = useContext(BaseViewsContext);
  const baseView = baseViews[CUSTOMER_LANE_COMMITMENTS];

  const [name, setName] = useState(board.name);
  const [isEntityFilterEnabled, setIsEntityFilterEnabled] = useState<boolean>(
    !!board.isEntityFilterEnabled,
  );
  const [isAsset, setIsAsset] = useState<boolean>(
    board ? !!board.isAsset : false,
  );
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [sortField, setSortField] = useState<string | undefined>(
    board.sortBy?.field,
  );
  const [sortDirection, setSortDirection] = useState<SortBy>(
    board.sortBy?.direction || 'asc',
  );

  const sortFieldOptions = useMemo(() => {
    return board.columnOrder
      .filter((col) => col.visible)
      .map((col) => ({
        field: col.field,
        label: baseView?.fields[col.field]?.nameAlias || col.field,
      }));
  }, [board.columnOrder, baseView]);

  const defaultFrom = DEFAULT_CUSTOMER_LANE_PERIODS_DATE_RANGE.from;
  const defaultTo = DEFAULT_CUSTOMER_LANE_PERIODS_DATE_RANGE.to;
  const [pastWeeks, setPastWeeks] = useState<number | undefined>(
    board.relativeDateRange?.from?.n ?? defaultFrom.n,
  );
  const [futureWeeks, setFutureWeeks] = useState<number | undefined>(
    board.relativeDateRange?.to?.n ?? defaultTo.n,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const onNameChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const onSave = () => {
    setIsLoading(true);
    const newBoard: CustomerLaneBoard = {
      ...board,
      name,
      isEntityFilterEnabled,
      isAsset,
      labelIds,
      sortBy: sortField
        ? { field: sortField, direction: sortDirection }
        : undefined,
      relativeDateRange: {
        from: {
          ...defaultFrom,
          n: pastWeeks ?? defaultFrom.n,
        },
        to: {
          ...defaultTo,
          n: futureWeeks ?? defaultTo.n,
        },
        config: DEFAULT_CUSTOMER_LANE_PERIODS_DATE_RANGE.config,
      },
    };
    updateBoard(newBoard, accountRef).then(() => {
      setIsLoading(false);
      close();
    });
  };

  return (
    <SettingsPopup
      name={name}
      onNameChanged={onNameChanged}
      isEntityFilterEnabled={isEntityFilterEnabled}
      setIsEntityFilterEnabled={setIsEntityFilterEnabled}
      isAsset={isAsset}
      setIsAsset={setIsAsset}
      labelIds={labelIds}
      setLabelIds={setLabelIds}
      sortField={sortField}
      setSortField={setSortField}
      sortDirection={sortDirection}
      setSortDirection={setSortDirection}
      sortFieldOptions={sortFieldOptions}
      pastWeeks={pastWeeks}
      setPastWeeks={setPastWeeks}
      futureWeeks={futureWeeks}
      setFutureWeeks={setFutureWeeks}
      onSave={onSave}
      isLoading={isLoading}
      close={close}
    />
  );
};

const Gate = ({ isOpen, close }: { isOpen: boolean; close: () => void }) => {
  const { board } = useContext(BoardContext);
  if (isCustomerLaneBoard(board)) {
    return (
      <ModalTransition>
        {isOpen && (
          <ModalDialog
            onClose={close}
            shouldScrollInViewport={false}
            autoFocus={false}
          >
            <SettingsPopupContainer close={close} board={board} />
          </ModalDialog>
        )}
      </ModalTransition>
    );
  }

  return null;
};

export default Gate;
